import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { Features } from "./components/Features";
import { Courses } from "./components/Courses";
import { AdditionalFeatures } from "./components/AdditionalFeatures";
import { Testimonials } from "./components/Testimonials";
import { LeadForm } from "./components/LeadForm";
import { FAQ } from "./components/FAQ";
import { LocationSection } from "./components/LocationSection";
import { Footer } from "./components/Footer";
import { WhatsAppWidget } from "./components/WhatsAppWidget";
import { AboutUs } from "./components/AboutUs";
import { SeminarSection } from "./components/SeminarSection";
import { HiringBanner } from "./components/HiringBanner";
import { AnnouncementBar } from "./components/AnnouncementBar";
import { FacilityGallery } from "./components/FacilityGallery";
import { Trainers } from "./components/Trainers";
import { StudentShorts } from "./components/StudentShorts";
import LeadsAdmin from "./pages/LeadsAdmin";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CoursePage from "./pages/CoursePage";
import HiringPartners from "./pages/HiringPartners";
import { applyPageMeta, buildFaqSchema } from "./lib/seo";
import { faqs as siteFaqs } from "./mock";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToForm = () => {
    const element = document.getElementById('lead-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // SEO: home page title/description/FAQ schema
  useEffect(() => {
    applyPageMeta({
      title: 'Video Editing Course in Bangalore | MatchFrame Editing School',
      description:
        "Bangalore's award-winning film & video editing institute. Avid, Premiere Pro, DaVinci Resolve, Final Cut Pro X. 750+ trained, 500+ placed, 125+ hiring partners. Apply now.",
      keywords:
        'video editing course Bangalore, film editing institute Bangalore, Adobe Premiere Pro course Bangalore, DaVinci Resolve course Bangalore, Avid course Bangalore, Final Cut Pro X Bangalore, AI video editing course Bangalore, MatchFrame, PRS Multimedia',
      path: '/',
      schemas: {
        'faq-jsonld': buildFaqSchema(siteFaqs.slice(0, 6)),
      },
    });
  }, []);

  // Handle direct navigation to sections via URL paths
  useEffect(() => {
    const sectionMap = {
      '/enquiry': 'lead-form',
      '/courses': 'courses',
      '/seminar': 'seminar',
      '/about': 'about-us',
      '/about-us': 'about-us',
      '/trainers': 'trainers',
      '/facility': 'facility',
      '/testimonials': 'testimonials',
      '/success-stories': 'testimonials',
      '/faq': 'faq',
      '/contact': 'contact',
      '/hiring': 'hiring'
    };

    const sectionId = sectionMap[location.pathname];
    if (sectionId) {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen">
      <AnnouncementBar />
      <Header onConsultationClick={scrollToForm} />
      <Hero onConsultationClick={scrollToForm} />
      <Features onEnrollClick={scrollToForm} />
      <section id="courses">
        <Courses onEnrollClick={scrollToForm} />
      </section>
      <section id="seminar">
        <SeminarSection />
      </section>
      <section id="about-us">
        <AboutUs />
      </section>
      <section id="trainers">
        <Trainers />
      </section>
      <section id="facility">
        <FacilityGallery />
      </section>
      <section id="student-shorts">
        <StudentShorts />
      </section>
      <AdditionalFeatures onEnrollClick={scrollToForm} />
      <section id="testimonials">
        <Testimonials />
      </section>
      <section id="lead-form">
        <LeadForm />
      </section>
      <section id="faq">
        <FAQ />
      </section>
      <section id="hiring">
        <HiringBanner />
      </section>
      <section id="contact">
        <LocationSection />
      </section>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10001] focus:px-4 focus:py-2 focus:bg-white focus:text-slate-900 focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Section Routes - All redirect to home with section scroll */}
          <Route path="/enquiry" element={<Home />} />
          <Route path="/courses" element={<Home />} />
          <Route path="/seminar" element={<Home />} />
          <Route path="/about" element={<Home />} />
          <Route path="/about-us" element={<Home />} />
          <Route path="/trainers" element={<Home />} />
          <Route path="/facility" element={<Home />} />
          <Route path="/testimonials" element={<Home />} />
          <Route path="/success-stories" element={<Home />} />
          <Route path="/faq" element={<Home />} />
          <Route path="/contact" element={<Home />} />
          <Route path="/hiring" element={<Home />} />
          {/* Course detail pages */}
          <Route path="/courses/:slug" element={<CoursePage />} />
          {/* Hiring Partners */}
          <Route path="/hiring-partners" element={<HiringPartners />} />
          {/* Admin & Legal Routes */}
          <Route path="/admin/leads" element={<LeadsAdmin />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          {/* 404 Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      {/* Global toast renderer — available on every route */}
      <Toaster />
    </div>
  );
}

export default App;
